import React from 'react';
import { AffiliationDocument, LocationDocument } from '../types';
import { haversineDistance } from '../lib/haversineDistance';
import QuestionMarkIcon from '../icons/QuestionMarkIcon';
import sanitizeHtml from 'sanitize-html';
import LocationIcon from '../icons/LocationIcon';

interface AffiliationItemProps {
	location: LocationDocument;
	affiliation: AffiliationDocument;
	currentLatitude: number;
	currentLongitude: number;
	hideDistance?: boolean;
	onClick?: (e: React.MouseEvent) => void;
}

const AffiliationItem: React.FC<AffiliationItemProps> = ({
	currentLatitude,
	currentLongitude,
	location,
	affiliation,
	onClick,
	hideDistance = false,
}) => {
	const [longitude, latitude] = location?.coordinates || [0, 0];
	const distance = haversineDistance(currentLatitude, currentLongitude, latitude, longitude);
	const image = affiliation.image ? (
		<div
			className="fi-affiliation-item__image"
			dangerouslySetInnerHTML={{ __html: affiliation.image }}
		></div>
	) : (
		<div className="fi-affiliation-item__missing-image"></div>
	);
	const permalink = `${window.findaConnector.affiliationBaseUrl}/${affiliation.slug}`;

	let hasExpeditedAppointment = affiliation.meta?.expedited_appointment;
	// TODO: Remove the line below when expedited appointment feature launches also convert let above to const.
	hasExpeditedAppointment = false;

	return (
		<div
			className="fi-affiliation-item"
			onClick={onClick}
			onKeyDown={() => null}
			role="button"
			tabIndex={0}
		>
			<div>{image}</div>
			<div className="fi-affiliation-item__details">
				<h3 className="fi-affiliation-item__title">{affiliation.title}</h3>
				{affiliation.locations && affiliation.locations.length > 0 && (
					<>
						<p
							className="doctor-card__location-title"
							dangerouslySetInnerHTML={{
								__html: sanitizeHtml(affiliation.locations[0].title),
							}}
						/>

						<p
							className="doctor-card__location-address"
							dangerouslySetInnerHTML={{
								__html: `${sanitizeHtml(affiliation.locations[0].meta.address)},
									${sanitizeHtml(affiliation.locations[0].meta.city)},
									${sanitizeHtml(affiliation.locations[0].meta.state)}`,
							}}
						/>
					</>
				)}
				<p className="doctor-card__procedures-text">Procedures Offered</p>
				{affiliation.tax.affiliation_attribute.length > 0 && (
					<p className="doctor-card__procedures">
						{affiliation.tax.affiliation_attribute
							.map((attribute) => sanitizeHtml(attribute.name))
							.join(', ')}
					</p>
				)}
				{hasExpeditedAppointment && (
					<>
						<a
							className="fi-affiliation-item__button"
							href={permalink + '#expedited-appointment-form'}
							data-button-text="Book an Expedited Appointment"
						>
							Book an Expedited Appointment
							<span></span>
						</a>
						<div className="tooltip">
							<QuestionMarkIcon />
							<div className="tooltip-content">
								<QuestionMarkIcon />
								<p className="tooltip-title">
									{sanitizeHtml(window.expeditedAppointmentTitle)}
								</p>
								<p className="tooltip-text">
									{sanitizeHtml(window.expeditedAppointmentText)}
								</p>
							</div>
						</div>
					</>
				)}
			</div>
			{!hideDistance && (
				<div className="fi-affiliation-item__distance">
					<LocationIcon />
					{isNaN(distance) ? '-.--' : distance.toFixed(2)}
					mi
				</div>
			)}
		</div>
	);
};

export default AffiliationItem;
